/**
 * Gate 2 — vector database connection card on the Execution Scope step.
 *
 * Connection host/index/provider and the secret are declared in Gate 1
 * readiness. This card shows that declaration read-only, or a warning with a
 * link back to Gate 1. Only Top K stays here as a run setting; a credential
 * field is an optional request override, not a replacement for the saved secret.
 */
import { Box, Button, Chip, TextField, Typography } from "@mui/material";
import { ROW_BTN_SX, SECTION_SUBTITLE_SX } from "./gate2Tokens";
import {
  SCOPE_CONNECTOR_CARD_HEADER_ROW_SX,
  SCOPE_CONNECTOR_CARD_TITLE_SX,
  SCOPE_METRICS_CARD_SX,
} from "./gate2ScopeTableStyles";
import {
  VECTOR_STORE_PROVIDER_OPTIONS,
  connectionSummaryRows,
  credentialFieldForProvider,
  sanitizeDigits,
} from "./gate2VectorForm";
import Gate2MissingConnectorBanner from "./Gate2MissingConnectorBanner";

const labelSx = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "20px",
  color: "#707070",
  mb: 0.5,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    boxShadow: "inset 0px 2px 3px 1px rgba(85, 85, 85, 0.2)",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#707070" },
    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: "1px" },
  },
};

function formatVerifiedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function providerLabel(provider) {
  return VECTOR_STORE_PROVIDER_OPTIONS.find((o) => o.value === provider)?.label || provider || "—";
}

export default function VectorDbConnectionCard({
  form,
  credentials = {},
  onFormChange,
  onCredentialsChange,
  disabled = false,
  gate1Source = null,
  onOpenGate1,
}) {
  const declared = Boolean(gate1Source?.available);
  const credentialField = credentialFieldForProvider(form.provider);
  const hasSavedSecret = Boolean(String(credentials?.[credentialField?.key] || "").trim());

  return (
    <Box sx={SCOPE_METRICS_CARD_SX}>
      <Box sx={SCOPE_CONNECTOR_CARD_HEADER_ROW_SX}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={SCOPE_CONNECTOR_CARD_TITLE_SX}>Vector Database Connection</Typography>
          <Typography sx={{ ...SECTION_SUBTITLE_SX, color: "#475569" }}>
            Using the vector store saved on Gate 1 — System & Evaluation Readiness.
          </Typography>
        </Box>
        {declared ? (
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
            <Button variant="outlined" disableElevation disabled={disabled} onClick={onOpenGate1} sx={ROW_BTN_SX}>
              Edit connection details
            </Button>
          </Box>
        ) : null}
      </Box>

      {declared ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              p: 2,
              borderRadius: "8px",
              bgcolor: gate1Source.ready ? "#F0FDF4" : "#EFF6FF",
              border: `1px solid ${gate1Source.ready ? "#BBF7D0" : "#BFDBFE"}`,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
              <Chip
                size="small"
                label={gate1Source.ready ? "Verified in Gate 1" : "Declared in Gate 1"}
                sx={{
                  height: 20,
                  borderRadius: "10px",
                  px: "8px",
                  fontFamily: "Lato, sans-serif",
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: gate1Source.ready ? "#DCFCE7" : "#DBEAFE",
                  color: gate1Source.ready ? "#15803D" : "#1D4ED8",
                  "& .MuiChip-label": { px: 0 },
                }}
              />
              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
                {providerLabel(form.provider)}
              </Typography>
              {formatVerifiedAt(gate1Source.verifiedAt) ? (
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#64748B" }}>
                  {formatVerifiedAt(gate1Source.verifiedAt)}
                </Typography>
              ) : null}
            </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.5,
              width: "100%",
            }}
          >
              {connectionSummaryRows(form).map((row) => (
                <Box key={row.key}>
                  <Typography
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                      color: "#94A3B8",
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontSize: 13,
                      color: "#383838",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={String(row.value)}
                  >
                    {String(row.value)}
                  </Typography>
                </Box>
              ))}
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start", width: "100%" }}>
              <Box sx={{ width: 140 }}>
                <Typography sx={labelSx}>Top K</Typography>
                <TextField
                  fullWidth
                  size="small"
                  disabled={disabled}
                  value={form.topK ?? ""}
                  onChange={(e) => onFormChange({ ...form, topK: sanitizeDigits(e.target.value) })}
                  sx={fieldSx}
                />
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#94A3B8", mt: 0.75 }}>
                  Chunks retrieved per test case.
                </Typography>
              </Box>
              {credentialField && onCredentialsChange ? (
                <Box sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}>
                  <Typography sx={labelSx}>{credentialField.label}</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    autoComplete="new-password"
                    disabled={disabled}
                    placeholder={
                      hasSavedSecret
                        ? "Saved in Gate 1 — type to override for this run"
                        : credentialField.placeholder || "Not stored — used for this run only"
                    }
                    value={credentials[credentialField.key] ?? ""}
                    onChange={(e) =>
                      onCredentialsChange({ ...credentials, [credentialField.key]: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Box>
              ) : null}
          </Box>
        </>
      ) : (
        <Gate2MissingConnectorBanner
          disabled={disabled}
          onOpenGate1={onOpenGate1}
          message={
            gate1Source?.message ||
            "No vector database connection has been verified yet. Run the vector store check in Gate 1 — System & Evaluation Readiness first."
          }
        />
      )}
    </Box>
  );
}
